import React, { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { StoreContext } from "../../../../../../../contexts";
import { APPLICATION_ROUTES } from "../../../../../../../constants";
import Loader from "../../../../../../atoms/Loader";
import classes from "./Zelle.module.scss";

const Zelle = observer(({ onClose }) => {
  const { checkout, cart, authorization } = useContext(StoreContext);
  const [copiedKey, setCopiedKey] = useState(null);

  const { zelleConfig, zelleData, zelleStep } = checkout;
  const isLoadingStart = checkout.isLoadingStartZelle;
  const isLoadingSubmit = checkout.isLoadingSubmitZelle;
  const startError = checkout.errorStartZelle;
  const submitError = checkout.errorSubmitZelle;

  useEffect(() => {
    if (checkout.id && zelleStep === "none" && !isLoadingStart) {
      checkout.startZelle();
    }
  }, [checkout.id, zelleStep]);

  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  const formatAmount = (num) => {
    if (typeof num !== "number") return "$0.00";
    return `$${num.toFixed(2)}`;
  };

  const handleGoHome = () => {
    const targetUrl = APPLICATION_ROUTES.HOME || "/";
    window.location.href = targetUrl;
  };

  const handleBack = () => {
    checkout.setValue("selectedPaymentMethod", "braintree");
    if (onClose) onClose();
  };

  if (isLoadingStart) {
    return (
      <div className={classes.card}>
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <Loader />
        </div>
      </div>
    );
  }

  if (startError) {
    return (
      <div className={classes.card}>
        {onClose && (
          <button type="button" className={classes.closeCrossBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
        <div className={classes.cardHeader}>
          <h3>Unable to Start Zelle Payment</h3>
          <p style={{ color: "#dc2626" }}>{startError}</p>
        </div>
        <div className={classes.footerBtnRow}>
          <button type="button" className={classes.backBtn} onClick={handleBack}>
            Back
          </button>
        </div>
      </div>
    );
  }

  // SCREEN 2: Thanks - we've got it
  if (
    zelleStep === "submitted" ||
    zelleData?.status === "submitted" ||
    zelleData?.status === "pending_zelle_verification"
  ) {
    const userName = authorization.user
      ? `${authorization.user.first_name || ""} ${authorization.user.last_name || ""}`.trim()
      : "Donor";
    const amountVal = zelleData?.amount ?? cart.total;
    const frequency = zelleData?.frequency;
    const freqSuffix = frequency ? ` / ${frequency?.charAt(0).toUpperCase() + frequency?.replace('_', ' ').slice(1)}` : "";
    const refCodeVal = zelleData?.reference_code || "";

    return (
      <div className={classes.confirmationContainer}>
        <button type="button" className={classes.closeCrossBtn} onClick={onClose || handleGoHome} aria-label="Close">
          ✕
        </button>
        <div className={classes.clockCircle}>🕒</div>

        <h2 className={classes.confirmTitle}>Thanks — we&apos;ve got it</h2>

        <p className={classes.confirmText}>
          We&apos;ll verify your Zelle payment and activate. This usually takes a short while. You&apos;ll get a confirmation email once it&apos;s active.
        </p>

        <div className={classes.summaryBox}>
          <div className={classes.summaryRow}>
            <span className={classes.sumLabel}>Pledged by</span>
            <span className={classes.sumVal}>{userName}</span>
          </div>

          <div className={classes.summaryRow}>
            <span className={classes.sumLabel}>Amount</span>
            <span className={classes.sumVal}>
              {formatAmount(amountVal)}{freqSuffix}
            </span>
          </div>

          <div className={classes.summaryRow}>
            <span className={classes.sumLabel}>Paid via</span>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div className={classes.zelleLogoIcon} style={{ width: "20px", height: "20px", fontSize: "0.75rem", borderRadius: "4px" }}>
                z
              </div>
              <span className={classes.sumVal}>Zelle</span>
            </div>
          </div>

          <div className={classes.summaryRow}>
            <span className={classes.sumLabel}>Reference code</span>
            <span className={classes.sumVal} style={{ fontFamily: "monospace", letterSpacing: "0.05em" }}>
              {refCodeVal}
            </span>
          </div>

          <div className={classes.summaryRow}>
            <span className={classes.sumLabel}>Status</span>
            <span className={classes.statusPendingBadge}>Pending verification</span>
          </div>
        </div>

        <p className={classes.footerNotice}>
          If you didn&apos;t include the reference code in your Zelle memo, reply to your confirmation email so we can match your payment faster.
        </p>

        <button type="button" className={classes.closeBtn} onClick={onClose || handleGoHome}>
          Close
        </button>
      </div>
    );
  }

  // SCREEN 1: Instructions View
  const email = zelleConfig?.email || "pledges@maor.org";
  const qrUrl = zelleConfig?.qr_url;
  const refCode = zelleData?.reference_code || "MA0R-8F3K2";
  const amount = zelleData?.amount ?? cart.total;

  return (
    <div className={classes.card}>
      {onClose && (
        <button type="button" className={classes.closeCrossBtn} onClick={onClose} aria-label="Close">
          ✕
        </button>
      )}
      <div className={classes.cardHeader}>
        <div className={classes.titleRow}>
          <div className={classes.zelleLogoIcon}>z</div>
          <h3>Pay with Zelle</h3>
        </div>
        <p className={classes.subtitle}>
          Send your payment via Zelle, then confirm below. We&apos;ll verify and activate.
        </p>
      </div>

      <div className={classes.bodyGrid}>
        <div className={classes.qrContainer}>
          <div className={classes.qrBox}>
            {qrUrl ? (
              <img src={qrUrl} alt="Zelle QR Code" />
            ) : (
              <img
                src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=Zelle-Payment"
                alt="Zelle QR Code"
              />
            )}
          </div>
          <span className={classes.qrLabel}>Scan in your bank app</span>
        </div>

        <div className={classes.detailsList}>
          <div className={classes.detailRow}>
            <span className={classes.label}>Send to</span>
            <div className={classes.copyableValue}>
              <span>{email}</span>
              <button
                type="button"
                className={classes.copyIconButton}
                title="Copy Zelle email"
                onClick={() => handleCopy(email, "email")}
              >
                {copiedKey === "email" ? "Copied!" : "❐"}
              </button>
            </div>
          </div>

          <div className={classes.detailRow}>
            <span className={classes.label}>Amount</span>
            <span className={classes.value}>{formatAmount(amount)}</span>
          </div>

          <div className={classes.detailRow}>
            <span className={classes.label}>Reference code</span>
            <span className={classes.refCodePill}>{refCode}</span>
          </div>

          <div className={classes.warningText}>
            <span className={classes.infoIcon}>ⓘ</span>
            <span>You must include this code in the Zelle memo so we can match your payment.</span>
          </div>
        </div>
      </div>

      {submitError && (
        <div className={classes.errorMessage}>
          {submitError}
        </div>
      )}

      <div className={classes.footerBtnRow}>
        <button type="button" className={classes.backBtn} onClick={handleBack}>
          Back
        </button>
        <button
          type="button"
          className={classes.submitBtn}
          disabled={isLoadingSubmit}
          onClick={() => checkout.submitZelle()}
        >
          {isLoadingSubmit ? (
            <Loader color="white" size="s" />
          ) : (
            <>
              <span>✓</span>
              <span>I&apos;ve sent the payment</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
});

export default Zelle;
